import React, { memo, useMemo, useState } from 'react';
import { Card, Alert, Button, List, Badge, Tooltip, Space, Tag } from 'antd';
import { 
  CloseOutlined, 
  ExclamationCircleOutlined, 
  WarningOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  BellOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { PERFORMANCE_CONFIG } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

const PerformanceAlerts = memo(({ 
  performanceData, 
  healthData, 
  settings = {},
  onDismissAlert,
  onUpdateSettings,
  className = '',
  style = {} 
}) => {
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());

  // Alert thresholds from settings or defaults
  const thresholds = {
    eventLoopLatency: settings.alertThresholds?.eventLoopLatency || PERFORMANCE_CONFIG.ALERT_THRESHOLDS.EVENT_LOOP_LATENCY.CRITICAL,
    memoryUsage: settings.alertThresholds?.memoryUsage || PERFORMANCE_CONFIG.ALERT_THRESHOLDS.MEMORY_USAGE.CRITICAL,
    cpuLoad: settings.alertThresholds?.cpuLoad || PERFORMANCE_CONFIG.ALERT_THRESHOLDS.CPU_LOAD.CRITICAL,
  };

  // Generate alerts based on performance data
  const alerts = useMemo(() => {
    const alertList = [];

    if (!performanceData) return alertList;

    const { eventLoopLatency, memory, cpu, requests } = performanceData;

    // Event Loop Latency Alert
    if (eventLoopLatency?.current > thresholds.eventLoopLatency) {
      alertList.push({
        id: 'event-loop-latency',
        type: 'error',
        severity: 'critical',
        title: 'High Event Loop Latency',
        description: `Event loop latency is ${eventLoopLatency.current.toFixed(2)}ms, exceeding threshold of ${thresholds.eventLoopLatency}ms`,
        timestamp: new Date().toISOString(),
        value: eventLoopLatency.current,
        threshold: thresholds.eventLoopLatency,
        unit: 'ms',
      });
    }

    // Memory Usage Alerts
    if (memory?.processHeap?.percentage > thresholds.memoryUsage) {
      alertList.push({
        id: 'process-heap-memory',
        type: 'error',
        severity: 'critical',
        title: 'High Process Heap Memory Usage',
        description: `Process heap memory usage is ${memory.processHeap.percentage.toFixed(1)}%, exceeding threshold of ${thresholds.memoryUsage}%`,
        timestamp: new Date().toISOString(),
        value: memory.processHeap.percentage,
        threshold: thresholds.memoryUsage,
        unit: '%',
      });
    }

    if (memory?.systemMemory?.percentage > thresholds.memoryUsage) {
      alertList.push({
        id: 'system-memory',
        type: 'error',
        severity: 'critical',
        title: 'High System Memory Usage',
        description: `System memory usage is ${memory.systemMemory.percentage.toFixed(1)}%, exceeding threshold of ${thresholds.memoryUsage}%`,
        timestamp: new Date().toISOString(),
        value: memory.systemMemory.percentage,
        threshold: thresholds.memoryUsage,
        unit: '%',
      });
    }

    // CPU Usage Alert
    if (cpu?.usage > thresholds.cpuLoad * 10) { // Convert to percentage
      alertList.push({
        id: 'cpu-usage',
        type: 'error',
        severity: 'critical',
        title: 'High CPU Usage',
        description: `CPU usage is ${cpu.usage.toFixed(1)}%, exceeding threshold of ${thresholds.cpuLoad * 10}%`,
        timestamp: new Date().toISOString(),
        value: cpu.usage,
        threshold: thresholds.cpuLoad * 10,
        unit: '%',
      });
    }

    // Request Error Rate Alert
    if (requests?.successRate < 95) {
      alertList.push({
        id: 'request-error-rate',
        type: 'warning',
        severity: 'warning',
        title: 'Low Request Success Rate',
        description: `Request success rate is ${requests.successRate.toFixed(1)}%, below recommended 95%`,
        timestamp: new Date().toISOString(),
        value: requests.successRate,
        threshold: 95,
        unit: '%',
      });
    }

    // Health Status Alerts
    if (healthData?.services) {
      Object.entries(healthData.services).forEach(([serviceName, service]) => {
        if (service?.status && service.status !== 'healthy') {
          alertList.push({
            id: `service-${serviceName}`,
            type: service.status === 'critical' ? 'error' : 'warning',
            severity: service.status === 'critical' ? 'critical' : 'warning',
            title: `${serviceName.charAt(0).toUpperCase() + serviceName.slice(1)} Service ${service.status}`,
            description: `${serviceName} service is ${service.status}${service.responseTime ? ` (${service.responseTime}ms response time)` : ''}`,
            timestamp: service.lastCheck || new Date().toISOString(),
            value: service.responseTime || 0,
            threshold: 0,
            unit: 'ms',
          });
        }
      });
    }

    return alertList.filter(alert => !dismissedAlerts.has(alert.id));
  }, [performanceData, healthData, thresholds, dismissedAlerts]);

  // Dismiss alert
  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
    if (onDismissAlert) {
      onDismissAlert(alertId);
    }
  };

  // Dismiss all alerts
  const handleDismissAll = () => {
    const allAlertIds = alerts.map(alert => alert.id);
    setDismissedAlerts(prev => new Set([...prev, ...allAlertIds]));
  };

  // Get alert icon
  const getAlertIcon = (type, severity) => {
    if (type === 'error' || severity === 'critical') {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    } else if (type === 'warning' || severity === 'warning') {
      return <WarningOutlined style={{ color: '#faad14' }} />;
    } else {
      return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  // Get alert color
  const getAlertColor = (type, severity) => {
    if (type === 'error' || severity === 'critical') {
      return '#ff4d4f';
    } else if (type === 'warning' || severity === 'warning') {
      return '#faad14';
    } else {
      return '#1890ff';
    }
  };

  // Alert item component
  const AlertItem = ({ alert }) => (
    <div className="alert-item">
      <div className="alert-item-header">
        <div className="alert-item-icon">
          {getAlertIcon(alert.type, alert.severity)}
        </div>
        <div className="alert-item-content">
          <div className="alert-item-title">{alert.title}</div>
          <div className="alert-item-description">{alert.description}</div>
        </div>
        <div className="alert-item-actions">
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleDismissAlert(alert.id)}
            className="alert-dismiss-btn"
          />
        </div>
      </div>
      <div className="alert-item-footer">
        <div className="alert-item-metrics">
          <Tag color={getAlertColor(alert.type, alert.severity)}>
            {alert.value.toFixed(1)}{alert.unit} / {alert.threshold}{alert.unit}
          </Tag>
        </div>
        <div className="alert-item-timestamp">
          {new Date(alert.timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );

  // Group alerts by severity
  const criticalAlerts = alerts.filter(alert => alert.severity === 'critical');
  const warningAlerts = alerts.filter(alert => alert.severity === 'warning');
  const infoAlerts = alerts.filter(alert => alert.severity === 'info');

  return (
    <Card 
      title={
        <div className="alerts-title">
          <BellOutlined />
          <span>Performance Alerts</span>
          <Badge count={alerts.length} showZero={false} />
        </div>
      }
      className={`performance-alerts ${className}`}
      style={style}
      extra={
        alerts.length > 0 && (
          <Space>
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => onUpdateSettings && onUpdateSettings()}
            >
              Settings
            </Button>
            <Button
              type="text"
              size="small"
              onClick={handleDismissAll}
            >
              Dismiss All
            </Button>
          </Space>
        )
      }
    >
      <div className="performance-alerts-content">
        {alerts.length === 0 ? (
          <div className="alerts-no-data">
            <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            <p>No active alerts</p>
            <p className="alerts-no-data-subtitle">All systems are running normally</p>
          </div>
        ) : (
          <div className="alerts-list">
            {/* Critical Alerts */}
            {criticalAlerts.length > 0 && (
              <div className="alerts-section">
                <h4 className="alerts-section-title critical">
                  <ExclamationCircleOutlined />
                  Critical Alerts ({criticalAlerts.length})
                </h4>
                <List
                  dataSource={criticalAlerts}
                  renderItem={(alert) => (
                    <List.Item className="alert-list-item critical">
                      <AlertItem alert={alert} />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Warning Alerts */}
            {warningAlerts.length > 0 && (
              <div className="alerts-section">
                <h4 className="alerts-section-title warning">
                  <WarningOutlined />
                  Warning Alerts ({warningAlerts.length})
                </h4>
                <List
                  dataSource={warningAlerts}
                  renderItem={(alert) => (
                    <List.Item className="alert-list-item warning">
                      <AlertItem alert={alert} />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Info Alerts */}
            {infoAlerts.length > 0 && (
              <div className="alerts-section">
                <h4 className="alerts-section-title info">
                  <InfoCircleOutlined />
                  Info Alerts ({infoAlerts.length})
                </h4>
                <List
                  dataSource={infoAlerts}
                  renderItem={(alert) => (
                    <List.Item className="alert-list-item info">
                      <AlertItem alert={alert} />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
});

PerformanceAlerts.displayName = 'PerformanceAlerts';

export default PerformanceAlerts;
